export type AuthFlowMode = "login" | "signup";

export interface AuthRedirectDetails {
  authParams: Record<string, string>;
  error: string | null;
  hasAuthPayload: boolean;
  mode: AuthFlowMode;
  returnTo: string;
}

interface BuildAuthCallbackUrlOptions {
  callbackPath?: string;
  currentHref?: string;
  mode?: AuthFlowMode;
  preferredOrigin?: string;
  relayId?: string;
  surface?: string;
}

const DEFAULT_APP_ORIGIN = "https://fintheon.pricedinresearch.io";
const DEFAULT_CALLBACK_PATH = "/auth/callback";

export function buildAuthCallbackUrl({
  callbackPath = DEFAULT_CALLBACK_PATH,
  currentHref,
  mode = "login",
  preferredOrigin,
  relayId,
  surface,
}: BuildAuthCallbackUrlOptions = {}): string {
  const currentUrl = readCurrentUrl(currentHref);
  const origin = normalizeOrigin(preferredOrigin) ?? currentUrl.origin;
  const callbackUrl = new URL(callbackPath, origin);

  callbackUrl.searchParams.set("return_to", buildReturnTo(currentUrl));
  callbackUrl.searchParams.set("mode", mode);
  if (relayId) callbackUrl.searchParams.set("relay_id", relayId);
  if (surface) callbackUrl.searchParams.set("surface", surface);

  return callbackUrl.toString();
}

export function readAuthRedirectDetails(href?: string): AuthRedirectDetails {
  const url = readCurrentUrl(href);
  const authParams: Record<string, string> = {};

  url.searchParams.forEach((value, key) => {
    if (isAuthParam(key)) authParams[key] = value;
  });

  const hashParams = new URLSearchParams(url.hash.replace(/^#/, ""));
  hashParams.forEach((value, key) => {
    if (isAuthParam(key)) authParams[key] = value;
  });

  const error =
    url.searchParams.get("error_description") ??
    url.searchParams.get("error") ??
    hashParams.get("error_description") ??
    hashParams.get("error");
  const mode = readMode(url.searchParams.get("mode"));
  const returnTo = normalizeReturnTo(url.searchParams.get("return_to"));

  return {
    authParams,
    error,
    hasAuthPayload:
      Boolean(authParams.code) ||
      Boolean(authParams.access_token && authParams.refresh_token) ||
      Boolean(error),
    mode,
    returnTo,
  };
}

export function cleanAuthRedirectUrl(returnTo: string): void {
  if (typeof window === "undefined") return;
  window.history.replaceState({}, "", normalizeReturnTo(returnTo));
}

export function isElectronRuntime(): boolean {
  if (typeof window === "undefined") return false;
  return Boolean((window as Window & { electron?: unknown }).electron);
}

function buildReturnTo(currentUrl: URL): string {
  if (!["http:", "https:"].includes(currentUrl.protocol)) {
    return normalizeReturnTo(`/${currentUrl.search}${currentUrl.hash}`);
  }
  if (currentUrl.pathname === DEFAULT_CALLBACK_PATH) return "/";
  return normalizeReturnTo(
    `${currentUrl.pathname}${currentUrl.search}${currentUrl.hash}`,
  );
}

function isAuthParam(key: string): boolean {
  return [
    "access_token",
    "code",
    "expires_at",
    "expires_in",
    "refresh_token",
    "token_type",
    "type",
  ].includes(key);
}

function normalizeOrigin(value?: string): string | null {
  if (!value) return null;
  try {
    return new URL(value).origin;
  } catch {
    return null;
  }
}

function normalizeReturnTo(value: string | null): string {
  if (!value || !value.startsWith("/") || value.startsWith("//")) return "/";
  if (value.includes("\\") || value.includes("\n") || value.includes("\r")) {
    return "/";
  }
  return value;
}

function readCurrentUrl(href?: string): URL {
  const fallback =
    typeof window === "undefined" ? DEFAULT_APP_ORIGIN : window.location.href;
  return new URL(href || fallback, DEFAULT_APP_ORIGIN);
}

function readMode(value: string | null): AuthFlowMode {
  return value === "signup" ? "signup" : "login";
}
